import { styled } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import { ProposalFieldCategoryType, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

const BadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
}));

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    fontSize: "0.6rem",
    height: "16px",
    minWidth: "16px",
  },
}));

type ProposalIssuesBadgeProps = {
  issues?: ProposalIssues;
  commentThreads?: CommentThread[];
  category: ProposalFieldCategoryType;
  title?: string;
};

export default function ProposalIssuesBadge(props: Readonly<ProposalIssuesBadgeProps>) {
  const { issues, commentThreads, category, title } = props;

  const filteredThreads = commentThreads?.filter(
    (comment) => comment.field.category === category && comment.field.name !== ProposalFieldName.General,
  );
  const filteredIssues = issues?.entries.filter((issue) => issue.field.category === category);
  const numErrors = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Critical).length ?? 0;
  const numWarnings = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Warning).length ?? 0;
  const numComments = filteredThreads?.reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  function getToolTip() {
    if (!filteredIssues && !filteredThreads) return "";

    const messages: React.ReactNode[] = [];

    if (filteredIssues) {
      for (const issue of filteredIssues) {
        messages.push(<p key={issue.field.name + (issue.field.id?.value ?? "")}>{issue.description}</p>);
      }
    }

    if (filteredThreads) {
      for (const thread of filteredThreads) {
        messages.push(
          <p key={thread.id?.value}>
            {thread.numUnreadMessages} new comment{thread.numUnreadMessages && thread.numUnreadMessages > 1 && "s"}.
          </p>,
        );
      }
    }

    return messages;
  }

  return (
    <BadgeContainer>
      <span>{title ?? category.toString()}</span>
      <Tooltip title={getToolTip()} placement="bottom">
        <BadgeContainer>
          {numErrors > 0 && <StyledBadge color="error" badgeContent={numErrors} />}
          {numWarnings > 0 && <StyledBadge color="warning" badgeContent={numWarnings} />}
          {numComments > 0 && <StyledBadge color="secondary" badgeContent={numComments} />}
        </BadgeContainer>
      </Tooltip>
    </BadgeContainer>
  );
}
